import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, ArrowLeft, CheckCircle, ArrowRight } from 'lucide-react';
import BrochureDownloadModal from '../components/BrochureDownloadModal';
import { products } from '../mockData';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find((p) => p.id === id);
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8F5]">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#004534] mb-4 font-['Outfit']">Product Not Found</h1>
          <Link to="/">
            <button className="px-6 py-3 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-[#C19B2E] transition-colors">
              Back to Home
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const handleCatalogueDownload = () => {
    setBrochureModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section - Dark Theme */}
      <section className="relative min-h-[70vh] bg-[#0A0D0C] pt-24 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        
        <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20 relative z-10">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="inline-flex items-center text-white/70 hover:text-[#D4AF37] transition-colors mb-8 group">
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="relative overflow-hidden">
                <img
                  src={product.hero}
                  alt={product.name}
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0C]/30 to-transparent" />
              </div>
              {/* Flagship Badge */}
              {product.flagship && (
                <div className="absolute top-4 left-4">
                  <span className="label-mono bg-[#D4AF37] text-[#0A0D0C] px-3 py-1.5 text-[10px]">
                    Flagship Product
                  </span>
                </div>
              )}
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="label-mono text-[#D4AF37] mb-4">Product Details</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit']">
                {product.name}
              </h1>
              <p className="text-xl text-[#D4AF37] font-medium mb-6">{product.tagline}</p>
              <p className="text-lg text-white/70 mb-10 leading-relaxed">{product.description}</p>
              
              <div className="flex flex-wrap gap-4">
                <motion.button
                  onClick={handleCatalogueDownload}
                  className="flex items-center px-6 py-3 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="product-download-catalogue"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Catalogue
                </motion.button>
                <Link to="/contact">
                  <motion.button
                    className="flex items-center px-6 py-3 border border-white/30 text-white hover:bg-white/10 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Request Sample
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Technical Specifications</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] font-['Outfit']">Key Features</h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-4 lg:gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {product.features.map((feature, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="flex items-start gap-4 p-6 bg-[#F7F8F5] border border-black/5 hover:border-[#D4AF37]/30 transition-colors"
              >
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#004534]">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <p className="text-[#004534] font-medium">{feature}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 lg:py-28 bg-[#0A0D0C]">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Applications</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit']">Perfect For</h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {product.applications.map((app, index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                className="p-6 bg-white/[0.03] border border-white/[0.08] text-center hover:bg-white/[0.06] hover:border-[#D4AF37]/30 transition-colors"
              >
                <p className="font-semibold text-white">{app}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Visual Reference</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] font-['Outfit']">Gallery</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {product.images.map((img, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="overflow-hidden group"
              >
                <img
                  src={img}
                  alt={`${product.name} ${index + 1}`}
                  className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#004534] relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#D4AF37]/10 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p 
              className="label-mono text-[#D4AF37] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Get Started
            </motion.p>
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Interested in {product.name}?
            </motion.h2>
            <motion.p 
              className="text-lg text-white/70 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Contact us today for product samples, pricing, and dealer information
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/contact">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2">
                  Get in Touch
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/dealer">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Become a Dealer
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Brochure Download Modal */}
      <BrochureDownloadModal
        isOpen={brochureModalOpen}
        onClose={() => setBrochureModalOpen(false)}
      />
    </div>
  );
};

export default ProductDetail;
